// Pure rekenfuncties voor de Uren / Weekstaat-module (module C3).
//
// Géén berekeningen in routes of componenten — alle datum-, week- en
// totalen-aggregatie voor weekstaten komt hier samen. Stijl volgt
// sick-leave-calculations: alle datums via UTC-midnight om DST/TZ-issues te
// vermijden, helpers privé, defensieve return bij ongeldige invoer.
//
// Conventies:
//   - ISO-week 8601: maandag is dag 1, week 1 bevat de eerste donderdag.
//   - Weekend = za (6) en zo (0) volgens `getUTCDay()`.
//   - Feestdagen worden vergeleken via dag-bucket-key `${y}-${m}-${d}`.

// ────────────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────────────

import { verdeelUrenOverDagen, type SplittableEntry } from './worked-hours-split'

const MS_PER_DAY = 1000 * 60 * 60 * 24

/** Normaliseer een Date naar UTC-midnight. */
function toUtcMidnight(date: Date): Date {
  return new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()))
}

/** Dag-bucket-key voor feestdag-vergelijking. */
function dayKey(date: Date): string {
  return `${date.getUTCFullYear()}-${date.getUTCMonth()}-${date.getUTCDate()}`
}

/** True als een datum een weekend (zaterdag of zondag) is in UTC. */
function isWeekend(date: Date): boolean {
  const dow = date.getUTCDay()
  return dow === 0 || dow === 6
}

// ────────────────────────────────────────────────────────────────────────────
// ISO-week functies
// ────────────────────────────────────────────────────────────────────────────

/**
 * Bereken ISO-jaar + ISO-weeknummer voor een datum.
 *
 * Belangrijk: het ISO-jaar kan afwijken van het kalenderjaar rond de
 * jaarwisseling — 2026-01-01 (donderdag) is week 1 van 2026, maar
 * 2025-12-29 t/m 2025-12-31 vallen ook in week 1 van 2026 onder ISO 8601.
 *
 * Bron: ISO 8601 §4.3.2, identiek aan algoritme in iso-week.weekStringFromDate.
 */
export function getIsoWeek(date: Date): { year: number; week: number } {
  const target = toUtcMidnight(date)
  const dow = target.getUTCDay() || 7 // 1=Mon..7=Sun
  // Verschuif naar de donderdag van de huidige week — die bepaalt het ISO-jaar.
  target.setUTCDate(target.getUTCDate() + 4 - dow)
  const year = target.getUTCFullYear()
  const jan1 = new Date(Date.UTC(year, 0, 1))
  const week = Math.ceil(((target.getTime() - jan1.getTime()) / MS_PER_DAY + 1) / 7)
  return { year, week }
}

/**
 * Maandag (start) en zondag (eind) van een ISO-week, beide UTC-midnight.
 * Eind valt op zondag 00:00:00 UTC — voor lte-queries kun je altijd op
 * `< maandag-van-volgende-week` testen om DST-veiligheid te garanderen.
 *
 * Bron: ISO 8601 §4.3.2 — ma-zo, donderdag in week 1.
 */
export function getWeekDateRange(year: number, week: number): { start: Date; end: Date } {
  // ISO 8601: 4 januari valt altijd in week 1.
  const jan4 = new Date(Date.UTC(year, 0, 4))
  const jan4Dow = jan4.getUTCDay() || 7 // 1=Mon..7=Sun
  const mondayOfWeek1 = new Date(jan4)
  mondayOfWeek1.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1))
  const start = new Date(mondayOfWeek1)
  start.setUTCDate(mondayOfWeek1.getUTCDate() + (week - 1) * 7)
  const end = new Date(start)
  end.setUTCDate(start.getUTCDate() + 6)
  return { start, end }
}

// ────────────────────────────────────────────────────────────────────────────
// Aggregatie-functies
// ────────────────────────────────────────────────────────────────────────────

/**
 * Som van uren / overuren / reisKm / reisMinuten over een set day-entries.
 * Null-waarden worden als 0 behandeld; uren / overuren worden afgerond op
 * 2 decimalen om floating-point drift te beperken.
 *
 * Bron: handover §5.5 weekstaat-totalen + fs-pro `weekstaat-totalen.ts`.
 */
export function calculateTotalsFromDayEntries(
  entries: Array<{
    hours: number | null
    overuren: number | null
    reisKm: number | null
    reisMinuten: number | null
  }>,
): { totalUren: number; totalOveruren: number; totalReisKm: number; totalReisMin: number } {
  let totalUren = 0
  let totalOveruren = 0
  let totalReisKm = 0
  let totalReisMin = 0

  for (const e of entries) {
    if (e.hours != null) totalUren += e.hours
    if (e.overuren != null) totalOveruren += e.overuren
    if (e.reisKm != null) totalReisKm += e.reisKm
    if (e.reisMinuten != null) totalReisMin += e.reisMinuten
  }

  return {
    totalUren: Math.round(totalUren * 100) / 100,
    totalOveruren: Math.round(totalOveruren * 100) / 100,
    totalReisKm,
    totalReisMin,
  }
}

// ────────────────────────────────────────────────────────────────────────────
// Split-aware per-dag aggregatie (spec 23 — nachtdienst uren-splitsing)
// ────────────────────────────────────────────────────────────────────────────

/**
 * FO-473 - de pauze van één dagregel zoals de weekstaat hem per dag toont:
 * beide assen met herkomst (strings, zodat deze module de pauze-types niet
 * hoeft te kennen).
 */
export interface DayAggregatePauzeRegel {
  werkbonNumber: string
  pauzeMinutes: number
  pauzeVerloning: string
  pauzeFacturatieMinuten: number | null
  pauzeBron: string | null
  pauzeVerloningBron: string | null
  pauzeFacturatieBron: string | null
  pauzeHandmatig: boolean
  pauzeHandmatigFacturatie: boolean
  betaaldeUren: number
  factuurUren: number
}

export interface WeekstaatDayEntryInput extends SplittableEntry {
  overuren: number | null
  reisKm: number | null
  reisMinuten: number | null
  werkbonNumber: string
  /**
   * FO-473 - de FACTURABELE uren van deze dagregel (`facturabeleUren`).
   * Ontbreekt = gelijk aan de betaalde uren (`hours`).
   */
  factuurUren?: number
  /** FO-473 - de pauze-stand van deze dagregel (voor de weergave per dag). */
  pauze?: Omit<DayAggregatePauzeRegel, 'werkbonNumber' | 'betaaldeUren' | 'factuurUren'>
}

export interface DayAggregateRow {
  date: string
  /** Betaalde uren (de verloningsas). */
  uren: number
  /** FO-473 - facturabele uren van dezelfde dagregels (de facturatie-as). */
  factuurUren: number
  overuren: number
  reisKm: number
  reisMin: number
  werkbonNumbers: string[]
  /**
   * FO-473 - de pauze per dagregel die op deze dag START (zoals overuren en
   * reis: per dienst, niet per uur gesplitst). Alleen gevuld als de invoer hem
   * meegeeft.
   */
  pauzeRegels?: DayAggregatePauzeRegel[]
}

function ensureDayRow(map: Map<string, DayAggregateRow>, date: string): DayAggregateRow {
  let row = map.get(date)
  if (!row) {
    row = { date, uren: 0, factuurUren: 0, overuren: 0, reisKm: 0, reisMin: 0, werkbonNumbers: [] }
    map.set(date, row)
  }
  return row
}

/**
 * Aggregeert day-entries per kalenderdag, split-aware: de uren van een
 * over-middernacht dienst worden via `verdeelUrenOverDagen` toegerekend. Een
 * NACHTDIENST gaat in zijn geheel naar zijn einddag (zo 22:00-06:00 = maandag,
 * PO 2026-10-16); een late dienst over middernacht blijft over zijn werkelijke
 * kalenderdagen verdeeld. Het werkbonnummer
 * verschijnt op elke dag die de dienst raakt; overuren/reis blijven op de startdag
 * (per-dienst, niet per uur gesplitst). Uren/overuren afgerond op 2 decimalen.
 */
export function aggregateDayEntriesByDate(
  entries: WeekstaatDayEntryInput[],
): Map<string, DayAggregateRow> {
  const byDay = new Map<string, DayAggregateRow>()
  for (const e of entries) {
    const buckets = verdeelUrenOverDagen(e)
    if (buckets.length === 0) continue
    for (const b of buckets) {
      const row = ensureDayRow(byDay, b.datum)
      row.uren += b.uren
      if (e.werkbonNumber && !row.werkbonNumbers.includes(e.werkbonNumber)) {
        row.werkbonNumbers.push(e.werkbonNumber)
      }
    }
    // FO-473 - de facturabele uren volgen exact dezelfde dag-verdeling (een
    // pauze over middernacht verdeelt zich evenredig over beide dagen), zodat
    // de som per dienst gelijk blijft aan `facturabeleUren`.
    if (e.factuurUren === undefined || e.factuurUren === e.hours) {
      for (const b of buckets) ensureDayRow(byDay, b.datum).factuurUren += b.uren
    } else {
      for (const b of verdeelUrenOverDagen({ ...e, hours: e.factuurUren })) {
        ensureDayRow(byDay, b.datum).factuurUren += b.uren
      }
    }
    // overuren/reis blijven op de startdag (per-dienst, niet per uur gesplitst).
    const startRow = ensureDayRow(byDay, buckets[0].datum)
    startRow.overuren += e.overuren ?? 0
    startRow.reisKm += e.reisKm ?? 0
    startRow.reisMin += e.reisMinuten ?? 0
    if (e.pauze) {
      startRow.pauzeRegels = [
        ...(startRow.pauzeRegels ?? []),
        {
          ...e.pauze,
          werkbonNumber: e.werkbonNumber,
          betaaldeUren: Math.round((e.hours ?? 0) * 100) / 100,
          factuurUren: Math.round((e.factuurUren ?? e.hours ?? 0) * 100) / 100,
        },
      ]
    }
  }
  for (const row of byDay.values()) {
    row.uren = Math.round(row.uren * 100) / 100
    row.factuurUren = Math.round(row.factuurUren * 100) / 100
    row.overuren = Math.round(row.overuren * 100) / 100
  }
  return byDay
}

// ────────────────────────────────────────────────────────────────────────────
// Werkdag-helpers
// ────────────────────────────────────────────────────────────────────────────

/**
 * True als een datum een werkdag is (ma-vr, geen publieke feestdag).
 *
 * Bron: parallel aan sick-leave werkdagenInPeriode-helper.
 */
export function isWerkdag(date: Date, publicHolidays: Date[] = []): boolean {
  if (isWeekend(date)) return false
  const key = dayKey(date)
  for (const h of publicHolidays) {
    if (dayKey(h) === key) return false
  }
  return true
}

/**
 * Aantal werkdagen in een gegeven ISO-week (5 minus eventuele feestdagen
 * binnen ma-vr van die week).
 *
 * Bron: weekstaat-rapportage in handover §5.5 (verwachte werktijd per week).
 */
export function aantalWerkdagenInWeek(
  year: number,
  week: number,
  publicHolidays: Date[] = [],
): number {
  const { start } = getWeekDateRange(year, week)
  let count = 0
  for (let i = 0; i < 7; i++) {
    const d = new Date(start)
    d.setUTCDate(start.getUTCDate() + i)
    if (isWerkdag(d, publicHolidays)) count++
  }
  return count
}
